import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaBox, FaShoppingCart, FaUsers, FaDownload, FaFilter, 
  FaSpinner, FaFileExcel, FaChartBar, FaTimes, FaSearch, 
  FaCheckCircle, FaTrash, FaClock, FaHistory, FaExclamationCircle,
  FaCloudDownloadAlt, FaRedo
} from 'react-icons/fa';
import ReportService from '../../../service/admin/reportService';
import './reportStyles.css';

const Reports = () => {
  const [stats, setStats] = useState({ products: 0, orders: 0, users: 0 });
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [exportLogs, setExportLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ page: 1, totalPages: 1 });
  const [activeTab, setActiveTab] = useState('export'); // 'export' or 'history'

  const reportTypes = [
    { id: 'products', name: 'Products', desc: 'All product fields with variations', icon: FaBox, color: '#4472C4', count: stats.products },
    { id: 'orders', name: 'Orders', desc: 'Orders with items & shipping', icon: FaShoppingCart, color: '#70AD47', count: stats.orders },
    { id: 'users', name: 'Users', desc: 'User accounts & data', icon: FaUsers, color: '#ED7D31', count: stats.users }
  ];

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (activeTab === 'history') fetchExportLogs(); }, [activeTab]);

  // Poll for pending exports
  useEffect(() => {
    const pendingExports = exportLogs.filter(l => l.status === 'pending' || l.status === 'processing');
    if (pendingExports.length === 0) return;

    const interval = setInterval(() => { fetchExportLogs(logsPagination.page); }, 3000);
    return () => clearInterval(interval);
  }, [exportLogs, logsPagination.page]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [statsRes, optionsRes] = await Promise.all([
        ReportService.getStats(),
        ReportService.getFilterOptions()
      ]);
      if (statsRes?.status?.code === 200) setStats(statsRes.data);
      if (optionsRes?.status?.code === 200) setFilterOptions(optionsRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchExportLogs = async (page = 1) => {
    try {
      setLogsLoading(true);
      const res = await ReportService.getExportLogs(page, 15);
      if (res?.status?.code === 200) {
        setExportLogs(res.data.logs || []);
        setLogsPagination({ page: res.data.page, totalPages: res.data.totalPages });
      }
    } catch (error) { console.error('Error fetching logs:', error); }
    finally { setLogsLoading(false); }
  };

  const handleExport = async (reportType, appliedFilters = {}) => {
    try {
      const res = await ReportService.startExport(reportType, appliedFilters);
      if (res?.status?.code === 200) {
        setActiveTab('history');
        fetchExportLogs();
      }
    } catch (error) { console.error('Export error:', error); }
    setShowFilters(false);
  };

  const handleDownload = async (exportId) => {
    try {
      const res = await ReportService.getDownloadUrl(exportId);
      if (res?.status?.code === 200 && res.data.url) {
        window.open(res.data.url, '_blank');
      }
    } catch (error) { console.error('Download error:', error); }
  };

  const handleDelete = async (exportId) => {
    if (!window.confirm('Delete this export?')) return;
    try {
      await ReportService.deleteExport(exportId);
      fetchExportLogs(logsPagination.page);
    } catch (error) { console.error('Delete error:', error); }
  };

  const getReportLabel = (type) => ({ products: 'Products', orders: 'Orders', users: 'Users' }[type] || type);
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <FaCheckCircle className="rpt-status-icon rpt-status-completed" />;
      case 'processing': case 'pending': return <FaSpinner className="rpt-status-icon rpt-status-processing rpt-spin" />;
      case 'failed': return <FaExclamationCircle className="rpt-status-icon rpt-status-failed" />;
      default: return <FaClock className="rpt-status-icon" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({});
  const openFilterModal = (reportType) => { setActiveReport(reportType); setShowFilters(true); setFilters({}); };

  const renderFilterModal = () => {
    if (!showFilters || !activeReport) return null;
    return (
      <div className="rpt-modal-overlay" onClick={() => setShowFilters(false)}>
        <div className="rpt-modal-content" onClick={e => e.stopPropagation()}>
          <div className="rpt-modal-header">
            <h3><FaFilter /> Export {getReportLabel(activeReport)}</h3>
            <button className="rpt-modal-close" onClick={() => setShowFilters(false)}><FaTimes /></button>
          </div>
          <div className="rpt-modal-body">
            <div className="rpt-filter-group">
              <label>Date Range</label>
              <div className="rpt-date-range">
                <input type="date" value={filters.fromDate || ''} onChange={e => handleFilterChange('fromDate', e.target.value)} />
                <span>to</span>
                <input type="date" value={filters.toDate || ''} onChange={e => handleFilterChange('toDate', e.target.value)} />
              </div>
            </div>
            {activeReport === 'products' && (
              <>
                <div className="rpt-filter-group">
                  <label>Search</label>
                  <div className="rpt-search-input"><FaSearch /><input type="text" value={filters.search || ''} onChange={e => handleFilterChange('search', e.target.value)} placeholder="Offer ID or Title..." /></div>
                </div>
                <div className="rpt-filter-row">
                  <div className="rpt-filter-group"><label>Status</label>
                    <select value={filters.status || ''} onChange={e => handleFilterChange('status', e.target.value)}>
                      <option value="">All</option>{filterOptions.productStatuses?.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <div className="rpt-filter-group"><label>Brand</label>
                    <select value={filters.brand || ''} onChange={e => handleFilterChange('brand', e.target.value)}>
                      <option value="">All</option>{filterOptions.brands?.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}
            {activeReport === 'orders' && (
              <div className="rpt-filter-row">
                <div className="rpt-filter-group"><label>Status</label>
                  <select value={filters.status || ''} onChange={e => handleFilterChange('status', e.target.value)}>
                    <option value="">All</option>{filterOptions.orderStatuses?.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div className="rpt-filter-group"><label>Marketplace</label>
                  <select value={filters.marketplace || ''} onChange={e => handleFilterChange('marketplace', e.target.value)}>
                    <option value="">All</option>{filterOptions.marketplaces?.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
            )}
            {activeReport === 'users' && (
              <div className="rpt-filter-group"><label>User Type</label>
                <select value={filters.userType || ''} onChange={e => handleFilterChange('userType', e.target.value)}>
                  <option value="">All</option>{filterOptions.userTypes?.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="rpt-modal-footer">
            <button className="rpt-btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="rpt-btn-primary" onClick={() => handleExport(activeReport, filters)}><FaCloudDownloadAlt /> Start Export</button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="rpt-loading"><FaSpinner className="rpt-spin" /><p>Loading...</p></div>;

  return (
    <div className="rpt-container">
      <div className="rpt-header">
        <div className="rpt-header-content">
          <h1><FaChartBar /> Reports & Downloads</h1>
          <p>Export data to Excel and manage download history</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="rpt-stats-row">
        {[{ icon: FaBox, color: '#4472C4', value: stats.products, label: 'Products' },
          { icon: FaShoppingCart, color: '#70AD47', value: stats.orders, label: 'Orders' },
          { icon: FaUsers, color: '#ED7D31', value: stats.users, label: 'Users' }
        ].map((s, i) => (
          <div key={i} className="rpt-stat-card" style={{ borderColor: s.color }}>
            <s.icon style={{ color: s.color }} />
            <div className="rpt-stat-info">
              <span className="rpt-stat-value">{s.value?.toLocaleString()}</span>
              <span className="rpt-stat-label">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="rpt-tabs">
        <button className={`rpt-tab ${activeTab === 'export' ? 'rpt-tab-active' : ''}`} onClick={() => setActiveTab('export')}>
          <FaFileExcel /> New Export
        </button>
        <button className={`rpt-tab ${activeTab === 'history' ? 'rpt-tab-active' : ''}`} onClick={() => setActiveTab('history')}>
          <FaHistory /> Export History
          {exportLogs.filter(l => l.status === 'processing' || l.status === 'pending').length > 0 && (
            <span className="rpt-tab-badge"><FaSpinner className="rpt-spin" /></span>
          )}
        </button>
      </div>

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="rpt-export-section">
          <div className="rpt-cards-grid">
            {reportTypes.map(report => (
              <div key={report.id} className="rpt-report-card">
                <div className="rpt-card-header">
                  <div className="rpt-card-icon" style={{ backgroundColor: `${report.color}15` }}>
                    <report.icon style={{ color: report.color }} />
                  </div>
                  <div className="rpt-card-title">
                    <h3>{report.name} Report</h3>
                    <p>{report.desc}</p>
                  </div>
                </div>
                <div className="rpt-card-stats">
                  <FaFileExcel /> <span>{report.count?.toLocaleString()} records available</span>
                </div>
                <div className="rpt-card-actions">
                  <button className="rpt-btn-export" onClick={() => openFilterModal(report.id)} style={{ backgroundColor: report.color }}>
                    <FaFilter /> Filter & Export
                  </button>
                  <button className="rpt-btn-quick" onClick={() => handleExport(report.id, {})}>
                    <FaDownload /> Quick Export All
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="rpt-history-section">
          <div className="rpt-history-header">
            <h3><FaHistory /> Export History</h3>
            <button className="rpt-btn-refresh" onClick={() => fetchExportLogs(logsPagination.page)} disabled={logsLoading}>
              <FaRedo className={logsLoading ? 'rpt-spin' : ''} /> Refresh
            </button>
          </div>

          {logsLoading && exportLogs.length === 0 ? (
            <div className="rpt-loading-small"><FaSpinner className="rpt-spin" /> Loading...</div>
          ) : exportLogs.length === 0 ? (
            <div className="rpt-empty"><FaFileExcel /><p>No exports yet. Start by creating a new export.</p></div>
          ) : (
            <>
              <div className="rpt-logs-table">
                <table>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Report Type</th>
                      <th>Exported By</th>
                      <th>Filters Applied</th>
                      <th>Records</th>
                      <th>Size</th>
                      <th>Time</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportLogs.map(log => (
                      <tr key={log._id} className={`rpt-log-row rpt-log-${log.status}`}>
                        <td>{getStatusIcon(log.status)} <span className="rpt-status-text">{log.status}</span></td>
                        <td><span className="rpt-type-badge">{getReportLabel(log.reportType)}</span></td>
                        <td className="rpt-user-cell">{log.userId?.firstName || log.userId?.email || '-'} {log.userId?.lastName || ''}</td>
                        <td className="rpt-filters-cell" title={log.filterSummary}>{log.filterSummary || 'All Records'}</td>
                        <td>{log.totalRecords?.toLocaleString() || '-'}</td>
                        <td>{formatFileSize(log.fileSize)}</td>
                        <td>{log.processingTime ? `${log.processingTime}s` : '-'}</td>
                        <td>{formatDate(log.createdAt)}</td>
                        <td className="rpt-actions-cell">
                          {log.status === 'completed' && (
                            <button className="rpt-btn-download-sm" onClick={() => handleDownload(log._id)} title="Download">
                              <FaDownload />
                            </button>
                          )}
                          <button className="rpt-btn-delete-sm" onClick={() => handleDelete(log._id)} title="Delete">
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {logsPagination.totalPages > 1 && (
                <div className="rpt-pagination">
                  <button disabled={logsPagination.page <= 1} onClick={() => fetchExportLogs(logsPagination.page - 1)}>Previous</button>
                  <span>Page {logsPagination.page} of {logsPagination.totalPages}</span>
                  <button disabled={logsPagination.page >= logsPagination.totalPages} onClick={() => fetchExportLogs(logsPagination.page + 1)}>Next</button>
                </div>
              )}
            </>
          )}

          <div className="rpt-history-info">
            <p><FaClock /> Exports are automatically deleted after 30 days</p>
          </div>
        </div>
      )}

      {renderFilterModal()}
    </div>
  );
};

export default Reports;
